import { useContext, useMemo, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { AccountHoldingResponse } from "../../../../api/src/accounts/accounts.service";
import type { HouseholdsController } from "../../../../api/src/households/households.controller";
import type { UnpackResponse } from "../../../../api/src/lib";
import type { Transaction } from "../../../../api/src/transactions/lib";
import type { TransactionsController } from "../../../../api/src/transactions/transactions.controller";
import HoldingsByAccountTable, {
  HoldingsFieldMode,
} from "../../components/HoldingsByAccountTable";
import { deserializeModelSummaryPercents } from "../percents";
import { useFirmRiskProfiles } from "../../firm/lib";
import { useAuthenticatedFetchAllPages } from "../../lib/pagination";
import { HouseholdContext } from "./HouseholdInfo";
import { useQueryHouseholdHoldings } from "./lib";

type HouseholdHoldingsProps = {
  accountSelection: Record<string, boolean>;
  customTaxPeriodParams: string;
};

/**
 * Remounts the screen per household, so the transactions the page walk
 * collected for one household are never shown under another
 * (`lib/pagination.ts`).
 */
const HouseholdHoldings = (props: HouseholdHoldingsProps) => {
  const { householdId: householdIdStr } = useParams();

  return <HouseholdHoldingsOfHousehold key={householdIdStr} {...props} />;
};

const HouseholdHoldingsOfHousehold = ({
  accountSelection,
  customTaxPeriodParams,
}: HouseholdHoldingsProps) => {
  const { householdId: householdIdStr } = useParams();
  const householdId = parseInt(householdIdStr ?? "-1");
  const { household } = useContext(HouseholdContext);

  const [fieldMode, setFieldMode] = useState<HoldingsFieldMode>("value");

  const { data: dataHoldings, isPending: isPendingHoldings } =
    useQueryHouseholdHoldings(householdId, {
      customTaxPeriodParams,
      enabled: typeof householdIdStr !== "undefined",
    });

  // `GET /v1/households/:id/model-summary` answers a page, in account ID
  // order (APP-1653), and the table needs the model weights of every account,
  // so the walk keeps asking until it holds every one. Each weight comes on 0
  // to 100 and is read on 0 to 1 (`deserializeModelSummaryPercents`).
  const {
    rows: modelSummaryRows,
    hasEveryRow: hasEveryModelSummary,
    isError: isErrorModelSummaries,
  } = useAuthenticatedFetchAllPages<
    UnpackResponse<
      HouseholdsController["getHouseholdModelSummary"]
    >["data"][number]
  >(`/v1/households/${householdIdStr}/model-summary`);
  const modelSummaries = useMemo(
    () =>
      hasEveryModelSummary
        ? modelSummaryRows.map((summary) =>
            deserializeModelSummaryPercents(summary),
          )
        : [],
    [hasEveryModelSummary, modelSummaryRows],
  );

  // `GET /v1/transactions/household/:id` answers a page (APP-1652); the walk
  // in `useAuthenticatedFetchAllPages` keeps asking until it holds every
  // transaction `pagination.totalCount` reports.
  const {
    rows: transactionRows,
    hasEveryRow: hasEveryTransaction,
    isError: isErrorTransactions,
  } = useAuthenticatedFetchAllPages<
    UnpackResponse<
      TransactionsController["getTransactionsForHousehold"]
    >["data"][number]
  >(`/v1/transactions/household/${householdIdStr}`);

  // `/v1/firm/risk` answers a PAGE, not the whole collection (APP-1563).
  // `useFirmRiskProfiles` walks it; reading one response as the whole set would
  // drop every holding allocated only to a risk level past the first page, and
  // `tsc` cannot see that — a page and the whole array have the same element
  // type. `HouseholdHoldings.pagination.spec.tsx` is what holds it.
  const { riskProfiles } = useFirmRiskProfiles();

  // The route returns them in display_order (`firms.service.ts`,
  // `getFirmRiskProfilesPage`), and the walk concatenates the pages in the
  // order it asked for them, so the sequence survives.
  const riskCategories = useMemo(
    () => riskProfiles.map((profile) => profile.name),
    [riskProfiles],
  );

  const selectedAccounts = useMemo(
    () =>
      (household?.accounts ?? []).filter(
        (account) => accountSelection[account.id] === true,
      ),
    [accountSelection, household?.accounts],
  );
  const holdings: AccountHoldingResponse[] = useMemo(
    () =>
      (dataHoldings ?? [])
        .filter((account) => accountSelection[account.id] === true)
        .flatMap((account) => account.holdings),
    [accountSelection, dataHoldings],
  );
  const transactions: Transaction[] = useMemo(
    () =>
      transactionRows.filter(
        (transaction) =>
          accountSelection[transaction.accountId] === true &&
          (transaction.type === "Income" || transaction.type === "Expense"),
      ),
    [accountSelection, transactionRows],
  );

  return (
    <>
      <Row>
        <Col xxl={3} xl={4} md={6} className="mb-3">
          <Form.Select
            value={fieldMode}
            onChange={(ev) =>
              setFieldMode(ev.currentTarget.value as HoldingsFieldMode)
            }
          >
            <option value="value">Value</option>
            <option value="weight">Weight</option>
            <option value="modelWeight">Model Weight</option>
            <option value="modelWeightDifference">
              Current - Model Weight
            </option>
            <option value="unrealizedGainLoss">Unrealized Gain/Loss</option>
            <option value="unrealizedGainLossPercent">
              Unrealized Gain/Loss %
            </option>
            <option value="realizedGainLoss">Realized Gain/Loss</option>
            <option value="income">Income</option>
          </Form.Select>
        </Col>
      </Row>
      <Row>
        <Col>
          <HoldingsByAccountTable
            holdings={holdings}
            accounts={selectedAccounts}
            modelSummaries={modelSummaries}
            transactions={transactions}
            fieldMode={fieldMode}
            isLoading={
              isPendingHoldings ||
              (!hasEveryModelSummary && !isErrorModelSummaries) ||
              (!hasEveryTransaction && !isErrorTransactions)
            }
            riskCategories={riskCategories}
            keepZeroValueInModelWeight
            oneRowPerSymbol
          />
        </Col>
      </Row>
    </>
  );
};

export default HouseholdHoldings;
